import React, { useEffect, useMemo, useState } from "react";
import { FaArrowLeft, FaMapMarkerAlt, FaPen, FaPlus, FaTimes } from "react-icons/fa";

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const EMPTY_FORM = {
  id: null,
  full_name: "",
  phone_number: "",
  street_address: "",
  landmark: "",
  city: "",
  state: "",
  postal_code: "",
  country: "India",
  address_type: "HOME",
  is_default: false,
};

export default function MMAddressBook({ onClose }) {
  const [addresses, setAddresses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  const sortedAddresses = useMemo(
    () => [...addresses].sort((a, b) => Number(b?.is_default) - Number(a?.is_default)),
    [addresses]
  );

  const fetchMarsMartAddresses = () => {
    fetch(`${BASE_URL}/mars-mart/address`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.addresses)
          ? data.addresses
          : [];
        setAddresses(list);
      })
      .catch(() => {
        setAddresses([]);
      });
  };

  useEffect(() => {
    fetchMarsMartAddresses();
  }, []);

  const openAdd = () => {
    setFormData({ ...EMPTY_FORM });
    setShowForm(true);
  };

  const openEdit = (address) => {
    setFormData({ ...address });
    setShowForm(true);
  };

  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by this browser.");
      return;
    }

    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`;
          const response = await fetch(url);
          const data = await response.json();
          const addr = data?.address || {};
          setFormData((prev) => ({
            ...prev,
            street_address: `${addr.road || ""} ${addr.suburb || ""} ${addr.neighbourhood || ""}`.trim(),
            city: addr.city || addr.town || addr.village || "",
            state: addr.state || "",
            postal_code: addr.postcode || "",
            country: addr.country || "India",
          }));
        } catch (error) {
          alert("Could not fetch location details. Please enter manually.");
        } finally {
          setDetecting(false);
        }
      },
      () => {
        setDetecting(false);
        alert("Location permission denied.");
      }
    );
  };

  const handleInput = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSaveAsync = async (event) => {
    event.preventDefault();

    const payload = {
      ...formData,
      country: formData.country || "India",
    };

    try {
      const response = await fetch(
        formData.id
          ? `${BASE_URL}/mars-mart/address/${formData.id}`
          : `${BASE_URL}/mars-mart/address/add`,
        {
          method: formData.id ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save address");
      }

      setShowForm(false);
      fetchMarsMartAddresses();
    } catch (error) {
      alert("Failed to save address.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this address?")) return;
    try {
      const response = await fetch(`${BASE_URL}/mars-mart/address/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to delete");
      }
      fetchMarsMartAddresses();
    } catch (error) {
      setAddresses([]);
      alert("Failed to remove address.");
    }
  };

  const setDefault = async (address) => {
    try {
      const response = await fetch(`${BASE_URL}/mars-mart/address/${address.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...address,
          is_default: true,
        }),
      });
      if (!response.ok) {
        throw new Error("Failed to set default");
      }
      fetchMarsMartAddresses();
    } catch (error) {
      alert("Failed to set default address.");
    }
  };

  return (
    <div className="mm-local-address-page">
      <div className="mm-local-address-header">
        <button type="button" className="mm-local-back-btn" onClick={onClose}>
          <FaArrowLeft />
        </button>
        <h2>Your Addresses</h2>
      </div>

      <div className="mm-local-address-grid">
        <div className="mm-local-add-card" onClick={openAdd}>
          <FaPlus className="mm-local-plus-icon" />
          <span className="mm-local-add-text">Add Address</span>
        </div>

        {sortedAddresses.map((address) => (
          <div
            key={address.id}
            className={`mm-local-address-card ${address.is_default ? "mm-local-default-card" : ""}`}
          >
            {address.is_default && <span className="mm-local-default-badge">Default</span>}
            <button type="button" className="mm-local-edit-btn" onClick={() => openEdit(address)}>
              <FaPen />
            </button>

            <div className="mm-local-content">
              <div className="mm-local-name">{address.full_name}</div>
              <div className="mm-local-text">{address.street_address}</div>
              {address.landmark && <div className="mm-local-text">Landmark: {address.landmark}</div>}
              <div className="mm-local-text">
                {address.city}, {address.state} - <strong>{address.postal_code}</strong>
              </div>
              <div className="mm-local-text">{address.country || "India"}</div>
              <div className="mm-local-phone">Phone: {address.phone_number}</div>
            </div>

            <div className="mm-local-actions">
              {!address.is_default && (
                <button type="button" className="mm-local-link-btn" onClick={() => setDefault(address)}>
                  Set Default
                </button>
              )}
              {address.is_default && <span />}
              <button
                type="button"
                className="mm-local-link-btn mm-local-delete-btn"
                onClick={() => handleDelete(address.id)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="mm-local-form-overlay" onClick={() => setShowForm(false)}>
          <div className="mm-local-form-modal" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="mm-local-form-close" onClick={() => setShowForm(false)}>
              <FaTimes />
            </button>
            <h3 className="mm-local-form-title">{formData.id ? "Edit Address" : "Add New Address"}</h3>

            <button type="button" className="mm-local-gps-btn" onClick={handleDetectLocation} disabled={detecting}>
              <FaMapMarkerAlt /> {detecting ? "Detecting location..." : "Use my current location"}
            </button>

            <form className="mm-local-form" onSubmit={handleSaveAsync}>
              <div className="mm-local-form-group">
                <label>Full Name</label>
                <input className="mm-local-input" name="full_name" value={formData.full_name} onChange={handleInput} required />
              </div>

              <div className="mm-local-form-group">
                <label>Mobile Number</label>
                <input className="mm-local-input" name="phone_number" value={formData.phone_number} onChange={handleInput} required />
              </div>

              <div className="mm-local-form-group">
                <label>Flat, House no., Building, Company, Apartment</label>
                <input className="mm-local-input" name="street_address" value={formData.street_address} onChange={handleInput} required />
              </div>

              <div className="mm-local-form-group">
                <label>Landmark</label>
                <input className="mm-local-input" name="landmark" value={formData.landmark || ""} onChange={handleInput} />
              </div>

              <div className="mm-local-row">
                <div className="mm-local-form-group">
                  <label>City</label>
                  <input className="mm-local-input" name="city" value={formData.city} onChange={handleInput} required />
                </div>
                <div className="mm-local-form-group">
                  <label>State</label>
                  <input className="mm-local-input" name="state" value={formData.state} onChange={handleInput} required />
                </div>
              </div>

              <div className="mm-local-form-group">
                <label>Pincode</label>
                <input className="mm-local-input" name="postal_code" value={formData.postal_code} onChange={handleInput} required />
              </div>

              <label className="mm-local-default-row">
                <input type="checkbox" name="is_default" checked={formData.is_default} onChange={handleInput} />
                Make this my default address
              </label>

              <button type="submit" className="mm-place-order-btn">
                Save Address
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
